import type { CheckboxOnChangeArg, UtilsDashData } from '@library/types/utils';
import { useCallback, useState } from 'react';
import {
  useLazyGetCSVQuery,
  useLazyGetCSVStatusQuery
} from '@bo-services/utilsApi';
import { useSearchParams } from 'react-router-dom';

const useUtilDashboard = <T extends UtilsDashData>({
  data,
  path,
  defaultItemData,
  setSubmitMessage
}: {
  data: Array<T>;
  path: string;
  defaultItemData: T;
  setSubmitMessage: (message: string) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allItemsChecked, setAllItemsChecked] = useState<boolean>(false);
  const [idsForCSV, setIdsForCSV] = useState<Array<string>>([]);
  const [isGeneratingCSV, setIsGeneratingCSV] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isNewItem, setIsNewItem] = useState(false);
  const [currentItemData, setCurrentItemData] = useState<T>({} as T);

  const [getCSVStatus] = useLazyGetCSVStatusQuery();
  const [getCSV] = useLazyGetCSVQuery();

  const handleClickAllItemsCheckbox = useCallback(
    ({ checked }: CheckboxOnChangeArg) => {
      setAllItemsChecked(checked);
      const idsArray = checked
        ? idsForCSV.concat(data.map((item) => item.id))
        : [];
      setIdsForCSV(idsArray);
    },
    [data, idsForCSV, setIdsForCSV, setAllItemsChecked]
  );

  const handleClickCheckbox = useCallback(
    ({ id, checked }: { id: string; checked: boolean }) => {
      const idsArray = checked
        ? idsForCSV.concat(id)
        : idsForCSV.filter((e) => e !== id);

      setIdsForCSV(idsArray);
    },
    [idsForCSV, setIdsForCSV]
  );

  const handleCSVExportClick = useCallback(async () => {
    setIsGeneratingCSV(true);
    try {
      const exportResponse = await getCSV({
        ids: idsForCSV,
        path
      }).unwrap();
      const exportId = exportResponse.data.id;

      const waitForFileUrl = async (): Promise<string> => {
        const statusResponse = await getCSVStatus(exportId).unwrap();
        const fileUrl = statusResponse.data.attributes.file_url;

        if (fileUrl) {
          setIsGeneratingCSV(false);
          return fileUrl;
        }
        setTimeout(() => {}, 2000);
        return waitForFileUrl();
      };

      const fileUrl = await waitForFileUrl();
      const formattedURL = fileUrl.replace('http', 'https');

      const link = document.createElement('a');
      link.href = formattedURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error exporting CSV:', error);
    }
  }, [getCSV, getCSVStatus, idsForCSV, path]);

  const handleAddNewItemClick = useCallback(() => {
    setIsDetailsModalOpen(true);
    setIsNewItem(true);
    setCurrentItemData(defaultItemData);
  }, [
    setIsDetailsModalOpen,
    setIsNewItem,
    setCurrentItemData,
    defaultItemData
  ]);

  const handleCloseModal = useCallback(() => {
    // Remove the id from URL parameters when closing the modal
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('id');
    setSearchParams(newParams);

    setIsDetailsModalOpen(false);
    setIsNewItem(false);
    setSubmitMessage('');
  }, [
    setIsDetailsModalOpen,
    setIsNewItem,
    setSubmitMessage,
    searchParams,
    setSearchParams
  ]);

  const handleClickRow = useCallback(
    (id: string) => {
      // Add the id to URL parameters when opening a row
      const newParams = new URLSearchParams(searchParams);
      newParams.set('id', id);
      setSearchParams(newParams);

      setIsDetailsModalOpen(true);
      setCurrentItemData(data.find((item) => item.id === id) || ({} as T));
    },
    [
      data,
      setIsDetailsModalOpen,
      setCurrentItemData,
      searchParams,
      setSearchParams
    ]
  );

  return {
    allItemsChecked,
    handleAddNewItemClick,
    handleClickAllItemsCheckbox,
    handleClickCheckbox,
    handleClickRow,
    handleCloseModal,
    handleCSVExportClick,
    idsForCSV,
    isDetailsModalOpen,
    isNewItem,
    currentItemData,
    isGeneratingCSV
  };
};

export default useUtilDashboard;
